export const formatSummaryLabel = (key) => (
    key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
);


export const isUsefulSummaryValue = (value) => (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    !(Array.isArray(value) && value.length === 0)
);


export const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');


export const formatReportValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not available';
    if (typeof value === 'number') return Number.isFinite(value) ? value.toFixed(3).replace(/\.?0+$/, '') : 'Not available';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
};


export const reportPatientFields = [
    'center',
    'site',
    'age',
    'sex',
    'gender',
    'epilepsy_type',
    'epilepsy',
    'lesional',
    'engel',
    'engel_outcome',
    'outcome'
];


export const summarizePatient = (summary = {}) => (
    reportPatientFields
        .filter(key => isUsefulSummaryValue(summary[key]))
        .map(key => ({ label: formatSummaryLabel(key), value: formatReportValue(summary[key]) }))
);


export const reportCoordinateValue = (item, key) => {
    const value = item?.coordinates?.[key] ?? item?.[key];
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};


export const clinicalMarkerColor = (label) => ({
    seizure_onset: '#f59e0b',
    early_spread: '#a855f7',
    interictal_spikes: '#22c55e',
    brain_lesions: '#06b6d4',
    epileptic: '#ec4899'
}[label] || '#64748b');


// Color key for the clinical metadata overlay on the 3D brain. Shared by the
// floating legend so its swatches stay in sync with the rendered markers.
export const CLINICAL_OVERLAY_LEGEND = [
    { label: 'SOZ', color: '#ff2d95' },
    { label: 'Early spread', color: '#ffb000' },
    { label: 'Spikes', color: '#00d7ff' },
    { label: 'Lesion', color: '#b66dff' },
    { label: 'Epileptic', color: '#4dff88' },
];


export const zScoreMarkerColor = (zScore) => {
    const numeric = Number(zScore);
    if (!Number.isFinite(numeric)) return '#64748b';
    if (numeric > 0) return '#dc2626';
    if (numeric < 0) return '#2563eb';
    return '#64748b';
};


export const reportProjectionDefaults = {
    axial: { x: [-85, 85], y: [-115, 85], xLabelLeft: 'Left', xLabelRight: 'Right', yLabelTop: 'Anterior', yLabelBottom: 'Posterior' },
    lateral: { x: [-85, 85], y: [-65, 95], xLabelLeft: 'Left', xLabelRight: 'Right', yLabelTop: 'Superior', yLabelBottom: 'Inferior' },
    coronal: { x: [-115, 85], y: [-65, 95], xLabelLeft: 'Posterior', xLabelRight: 'Anterior', yLabelTop: 'Superior', yLabelBottom: 'Inferior' }
};


export const buildReportBrainOutlineSvg = ({ projection, width, height, pad }) => {
    const centerX = width / 2;
    const centerY = height / 2;
    const brainWidth = width - pad * 2;
    const brainHeight = height - pad * 2;

    if (projection === 'lateral') {
        const left = pad + 16;
        const right = width - pad - 12;
        const top = pad + 16;
        const bottom = height - pad - 16;
        const midY = centerY + 8;
        return `
            <path d="M ${left + 36} ${midY}
                     C ${left + 12} ${top + 42}, ${left + 86} ${top - 2}, ${centerX - 12} ${top + 8}
                     C ${right - 72} ${top + 2}, ${right - 12} ${top + 54}, ${right - 26} ${centerY - 4}
                     C ${right - 10} ${bottom - 54}, ${right - 86} ${bottom + 6}, ${centerX - 20} ${bottom - 12}
                     C ${left + 84} ${bottom - 2}, ${left + 16} ${bottom - 42}, ${left + 36} ${midY} Z"
                  fill="#111827" stroke="#94a3b8" stroke-width="2" />
            <path d="M ${left + 90} ${centerY - 26}
                     C ${centerX - 18} ${centerY - 52}, ${right - 98} ${centerY - 42}, ${right - 78} ${centerY + 12}
                     C ${right - 116} ${centerY + 54}, ${centerX + 10} ${centerY + 64}, ${left + 108} ${centerY + 42}"
                  fill="none" stroke="#475569" stroke-width="1.2" stroke-dasharray="6 7" opacity="0.9" />
            <path d="M ${centerX - 74} ${top + 48}
                     C ${centerX - 18} ${top + 70}, ${centerX + 34} ${top + 72}, ${centerX + 88} ${top + 48}"
                  fill="none" stroke="#334155" stroke-width="1" opacity="0.8" />
        `;
    }

    return `
        <path d="M ${centerX} ${pad + 10}
                 C ${pad + 18} ${pad + 20}, ${pad + 4} ${centerY - 18}, ${pad + 26} ${centerY + 18}
                 C ${pad + 22} ${height - pad - 34}, ${centerX - 46} ${height - pad + 6}, ${centerX} ${height - pad - 8}
                 C ${width - pad - 22} ${height - pad - 34}, ${width - pad - 4} ${centerY + 18}, ${width - pad - 26} ${centerY - 18}
                 C ${width - pad - 18} ${pad + 20}, ${centerX} ${pad + 10}, ${centerX} ${pad + 10} Z"
              fill="#111827" stroke="#94a3b8" stroke-width="2" />
        <path d="M ${centerX} ${pad + 16}
                 C ${centerX - 16} ${centerY - 44}, ${centerX - 12} ${centerY + 36}, ${centerX} ${height - pad - 16}"
              fill="none" stroke="#64748b" stroke-width="1.3" stroke-dasharray="6 7" opacity="0.95" />
        <path d="M ${centerX - brainWidth * 0.18} ${centerY - brainHeight * 0.11}
                 C ${centerX - brainWidth * 0.33} ${centerY - brainHeight * 0.02}, ${centerX - brainWidth * 0.25} ${centerY + brainHeight * 0.14}, ${centerX - brainWidth * 0.09} ${centerY + brainHeight * 0.08}"
              fill="none" stroke="#334155" stroke-width="1" opacity="0.8" />
        <path d="M ${centerX + brainWidth * 0.18} ${centerY - brainHeight * 0.11}
                 C ${centerX + brainWidth * 0.33} ${centerY - brainHeight * 0.02}, ${centerX + brainWidth * 0.25} ${centerY + brainHeight * 0.14}, ${centerX + brainWidth * 0.09} ${centerY + brainHeight * 0.08}"
              fill="none" stroke="#334155" stroke-width="1" opacity="0.8" />
    `;
};


export const buildCoordinateMapSvg = ({ points = [], clinicalContacts = [], title, subtitle, projection = 'axial' }) => {
    const projectionKeys = {
        axial: ['surfmm_x', 'surfmm_y'],
        lateral: ['surfmm_x', 'surfmm_z'],
        coronal: ['surfmm_y', 'surfmm_z']
    };
    const [xKey, yKey] = projectionKeys[projection] || projectionKeys.axial;
    const drawable = [
        ...points
            .map(point => ({
                ...point,
                x: reportCoordinateValue(point, xKey),
                y: reportCoordinateValue(point, yKey),
                kind: 'z'
            }))
            .filter(point => point.x !== null && point.y !== null),
        ...clinicalContacts
            .map(contact => ({
                ...contact,
                x: reportCoordinateValue(contact, xKey),
                y: reportCoordinateValue(contact, yKey),
                kind: 'clinical'
            }))
            .filter(point => point.x !== null && point.y !== null)
    ];

    if (drawable.length === 0) {
        return `
            <div class="map-card empty-map">
                <h3>${escapeHtml(title)}</h3>
                <p class="muted">No surface coordinates were available for this projection.</p>
            </div>
        `;
    }

    const width = 520;
    const height = 360;
    const pad = 54;
    const xs = drawable.map(point => point.x);
    const ys = drawable.map(point => point.y);
    const defaults = reportProjectionDefaults[projection] || reportProjectionDefaults.axial;
    const minX = Math.min(defaults.x[0], ...xs);
    const maxX = Math.max(defaults.x[1], ...xs);
    const minY = Math.min(defaults.y[0], ...ys);
    const maxY = Math.max(defaults.y[1], ...ys);
    const spanX = Math.max(maxX - minX, 1);
    const spanY = Math.max(maxY - minY, 1);
    const projectX = value => pad + ((value - minX) / spanX) * (width - pad * 2);
    const projectY = value => height - pad - ((value - minY) / spanY) * (height - pad * 2);
    const zPoints = drawable.filter(point => point.kind === 'z');
    const clinicalPoints = drawable.filter(point => point.kind === 'clinical');
    const labeledZPoints = [...zPoints]
        .filter(point => Number.isFinite(Number(point.z_score)))
        .sort((a, b) => Math.abs(Number(b.z_score)) - Math.abs(Number(a.z_score)))
        .slice(0, 8);

    const zPointSvg = zPoints.slice(0, 350).map(point => {
        const zScore = Number(point.z_score);
        const radius = Number.isFinite(zScore) ? Math.min(20, Math.max(5, 4 + Math.abs(zScore) * 2.3)) : 5;
        const stroke = Math.abs(zScore) >= 3 ? '#111827' : '#ffffff';
        return `<circle cx="${projectX(point.x).toFixed(1)}" cy="${projectY(point.y).toFixed(1)}" r="${radius.toFixed(1)}" fill="${zScoreMarkerColor(zScore)}" fill-opacity="0.82" stroke="${stroke}" stroke-width="1.4">
            <title>${escapeHtml(point.patient_id)} ${escapeHtml(point.channel)} ${escapeHtml(point.region_name || point.roi || '')} Z=${formatReportValue(zScore)}</title>
        </circle>`;
    }).join('');

    const zPointLabelSvg = labeledZPoints.map((point, index) => {
        const x = projectX(point.x);
        const y = projectY(point.y);
        const dx = x > width * 0.74 ? -78 : 14;
        const dy = index % 2 === 0 ? -12 : 18;
        const labelX = Math.max(12, Math.min(width - 88, x + dx));
        const labelY = Math.max(20, Math.min(height - 18, y + dy));
        return `
            <line x1="${x.toFixed(1)}" y1="${y.toFixed(1)}" x2="${labelX.toFixed(1)}" y2="${labelY.toFixed(1)}" stroke="#cbd5e1" stroke-width="1" opacity="0.72" />
            <text x="${labelX.toFixed(1)}" y="${labelY.toFixed(1)}" fill="#f8fafc" font-size="11" font-weight="700">${escapeHtml(point.channel || '')}</text>
        `;
    }).join('');

    const clinicalPointSvg = clinicalPoints.slice(0, 250).map(point => {
        const label = point.primary_label || point.labels?.[0]?.id || 'clinical';
        const x = projectX(point.x);
        const y = projectY(point.y);
        return `<path d="M ${x.toFixed(1)} ${(y - 8).toFixed(1)} L ${(x + 8).toFixed(1)} ${y.toFixed(1)} L ${x.toFixed(1)} ${(y + 8).toFixed(1)} L ${(x - 8).toFixed(1)} ${y.toFixed(1)} Z" fill="${clinicalMarkerColor(label)}" stroke="#111827" stroke-width="1.5">
            <title>${escapeHtml(point.patient_id)} ${escapeHtml(point.contact)} ${escapeHtml(point.region_name || point.roi || '')} ${escapeHtml(point.primary_label_display || label)}</title>
        </path>`;
    }).join('');

    return `
        <div class="map-card">
            <h3>${escapeHtml(title)}</h3>
            <p class="muted">${escapeHtml(subtitle)}</p>
            <svg viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeHtml(title)}">
                <rect x="0" y="0" width="${width}" height="${height}" rx="16" fill="#0f172a" />
                ${buildReportBrainOutlineSvg({ projection, width, height, pad })}
                <text x="${pad}" y="${height - 18}" fill="#94a3b8" font-size="11">${escapeHtml(defaults.xLabelLeft)}</text>
                <text x="${width - pad - 42}" y="${height - 18}" fill="#94a3b8" font-size="11">${escapeHtml(defaults.xLabelRight)}</text>
                <text x="${width - 92}" y="${pad - 18}" fill="#94a3b8" font-size="11">${escapeHtml(defaults.yLabelTop)}</text>
                <text x="${width - 94}" y="${height - pad + 26}" fill="#94a3b8" font-size="11">${escapeHtml(defaults.yLabelBottom)}</text>
                ${zPointSvg}
                ${clinicalPointSvg}
                ${zPointLabelSvg}
            </svg>
        </div>
    `;
};


export const reportClinicalFlags = (region = {}) => ([
    region.is_soz ? 'SOZ' : null,
    region.is_early_spread ? 'Early spread' : null,
    region.is_spikes ? 'Spikes' : null,
    region.is_lesion ? 'Lesion' : null,
    region.is_epileptic ? 'Epileptic' : null
].filter(Boolean));


export const buildClinicalReportHtml = ({
    generatedAt,
    patients,
    centers,
    feature,
    featureLabel,
    threshold,
    brainImage,
    patientSummaries,
    clinicalRegions,
    clinicalContacts = [],
    thresholdedChannels,
    zScorePoints,
    zScoreSettings
}) => {
    const topChannels = thresholdedChannels.slice(0, 40);
    const abnormalRegions = clinicalRegions
        .filter(region => region.is_soz || region.is_early_spread || region.is_spikes || region.is_lesion || region.is_epileptic)
        .slice(0, 40);
    const regionZSummary = zScorePoints.reduce((acc, point) => {
        const key = `${point.patient_id}::${point.region_name || point.roi || 'Unknown region'}`;
        const zScore = Number(point.z_score);
        if (!Number.isFinite(zScore)) return acc;
        if (!acc[key]) {
            acc[key] = {
                patient_id: point.patient_id,
                region_name: point.region_name || point.roi || 'Unknown region',
                n_channels: 0,
                max_abs_z: 0,
                max_z: zScore,
                min_z: zScore
            };
        }
        acc[key].n_channels += 1;
        acc[key].max_abs_z = Math.max(acc[key].max_abs_z, Math.abs(zScore));
        acc[key].max_z = Math.max(acc[key].max_z, zScore);
        acc[key].min_z = Math.min(acc[key].min_z, zScore);
        return acc;
    }, {});
    const topZRegions = Object.values(regionZSummary)
        .sort((a, b) => b.max_abs_z - a.max_abs_z)
        .slice(0, 25);
    const clinicalRegionByPatientAndName = new Map(
        abnormalRegions.flatMap(region => ([
            [`${region.patient_id}::${region.region_name}`, region],
            [`${region.patient_id}::${region.roi}`, region]
        ]))
    );
    const thresholdedPointSet = new Set(
        thresholdedChannels.map(channel => `${channel.patient_id}::${channel.channel}::${channel.feature}`)
    );
    const mapZPoints = zScorePoints
        .filter(point => thresholdedPointSet.has(`${point.patient_id}::${point.channel}::${point.feature}`))
        .slice(0, 350);
    const axialMapHtml = buildCoordinateMapSvg({
        points: mapZPoints,
        clinicalContacts,
        title: 'Axial Electrode Abnormality Map',
        subtitle: 'Red/blue circles are thresholded z-score electrodes; diamonds are clinical metadata labels.',
        projection: 'axial'
    });
    const lateralMapHtml = buildCoordinateMapSvg({
        points: mapZPoints,
        clinicalContacts,
        title: 'Lateral Electrode Abnormality Map',
        subtitle: 'Point size follows absolute z-score magnitude; clinical labels remain diamond markers.',
        projection: 'lateral'
    });
    const spotlightRegions = topZRegions
        .map(region => {
            const clinicalMatch = abnormalRegions.find(item => (
                item.patient_id === region.patient_id &&
                (item.region_name === region.region_name || item.roi === region.region_name)
            ));
            return {
                ...region,
                clinical: clinicalMatch || null,
                flags: reportClinicalFlags(clinicalMatch)
            };
        })
        .concat(
            abnormalRegions
                .filter(region => !topZRegions.some(item => (
                    item.patient_id === region.patient_id &&
                    item.region_name === region.region_name
                )))
                .map(region => ({
                    patient_id: region.patient_id,
                    region_name: region.region_name,
                    n_channels: region.contact_count || 0,
                    max_abs_z: null,
                    max_z: null,
                    min_z: null,
                    clinical: region,
                    flags: reportClinicalFlags(region)
                }))
        )
        .slice(0, 10);
    const labeledHotChannels = topChannels.slice(0, 8);
    const clinicalOverlapChannels = topChannels
        .map(channel => ({
            ...channel,
            clinical: clinicalRegionByPatientAndName.get(`${channel.patient_id}::${channel.region_name}`) ||
                clinicalRegionByPatientAndName.get(`${channel.patient_id}::${channel.roi}`)
        }))
        .filter(channel => channel.clinical);
    const topRegionNames = topZRegions.slice(0, 5).map(region => region.region_name).filter(Boolean);
    const topClinicalFlags = clinicalOverlapChannels
        .flatMap(channel => reportClinicalFlags(channel.clinical))
        .filter(Boolean);
    const uniqueClinicalFlags = [...new Set(topClinicalFlags)];
    const localizationSummaryHtml = `
        <section class="localization-summary">
            <div>
                <span class="eyebrow">Most abnormal feature</span>
                <strong>${escapeHtml(featureLabel || feature)}</strong>
                <p class="muted">Showing channels with |Z| >= ${escapeHtml(threshold)}. Circle size follows absolute z-score magnitude.</p>
            </div>
            <div>
                <span class="eyebrow">Highest-ranked regions</span>
                <strong>${escapeHtml(topRegionNames.join(', ') || 'No localized z-score regions')}</strong>
                <p class="muted">${topZRegions.length ? `${topZRegions.length} regions had mapped z-score values.` : 'No surface-mapped abnormal regions were available.'}</p>
            </div>
            <div>
                <span class="eyebrow">Clinical overlap</span>
                <strong>${escapeHtml(uniqueClinicalFlags.join(', ') || 'No SOZ/spike overlap detected')}</strong>
                <p class="muted">${clinicalOverlapChannels.length} top channels overlap a clinically labeled abnormal region.</p>
            </div>
        </section>
    `;

    const patientSummaryHtml = patientSummaries.length
        ? patientSummaries.map(summary => {
            const fields = summarizePatient(summary);
            const labelCounts = summary.clinical_labels || {};
            return `
                <section class="card">
                    <h3>${escapeHtml(summary.patient_id)}</h3>
                    <div class="kv-grid">
                        ${fields.map(item => `
                            <div><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong></div>
                        `).join('')}
                    </div>
                    <div class="label-row">
                        ${Object.entries(labelCounts)
                            .filter(([, value]) => Number(value) > 0)
                            .map(([key, value]) => `<span>${escapeHtml(formatSummaryLabel(key))}: ${escapeHtml(value)}</span>`)
                            .join('')}
                    </div>
                </section>
            `;
        }).join('')
        : '<p class="muted">No patient metadata was available for the selected cohort.</p>';

    const topChannelsHtml = topChannels.length
        ? topChannels.map(channel => `
            <tr>
                <td>${escapeHtml(channel.patient_id)}</td>
                <td>${escapeHtml(channel.channel)}</td>
                <td>${escapeHtml(channel.region_name)}</td>
                <td class="${channel.z_score >= 0 ? 'positive' : 'negative'}">${formatReportValue(channel.z_score)}</td>
                <td>${formatReportValue(channel.raw_value)}</td>
                <td>${formatReportValue(channel.coordinates?.surfmm_x)}, ${formatReportValue(channel.coordinates?.surfmm_y)}, ${formatReportValue(channel.coordinates?.surfmm_z)}</td>
            </tr>
        `).join('')
        : '<tr><td colspan="6" class="muted">No channels exceeded the selected threshold.</td></tr>';
    const labeledHotChannelsHtml = labeledHotChannels.length
        ? labeledHotChannels.map((channel, index) => {
            const clinicalMatch = clinicalRegionByPatientAndName.get(`${channel.patient_id}::${channel.region_name}`) ||
                clinicalRegionByPatientAndName.get(`${channel.patient_id}::${channel.roi}`);
            const flags = reportClinicalFlags(clinicalMatch);
            return `
                <div class="hot-channel-card">
                    <span class="rank">${index + 1}</span>
                    <div>
                        <strong>${escapeHtml(channel.channel)}</strong>
                        <p>${escapeHtml(channel.patient_id)} - ${escapeHtml(channel.region_name || 'Unknown region')}</p>
                    </div>
                    <div class="hot-channel-score ${channel.z_score >= 0 ? 'positive' : 'negative'}">${formatReportValue(channel.z_score)}</div>
                    <div class="label-row mini-labels">
                        ${(flags.length ? flags : ['No clinical flag']).map(flag => `<span class="${flag === 'SOZ' ? 'soz-pill' : ''}">${escapeHtml(flag)}</span>`).join('')}
                    </div>
                </div>
            `;
        }).join('')
        : '<p class="muted">No thresholded channels were available for a hot-channel summary.</p>';

    const clinicalRegionsHtml = abnormalRegions.length
        ? abnormalRegions.map(region => `
            <tr>
                <td>${escapeHtml(region.patient_id)}</td>
                <td>${escapeHtml(region.region_name)}</td>
                <td>${escapeHtml(region.lobe || 'Unknown')}</td>
                <td>${escapeHtml(region.contact_count)}</td>
                <td>${region.is_soz ? 'Yes' : 'No'}</td>
                <td>${region.is_early_spread ? 'Yes' : 'No'}</td>
                <td>${region.is_spikes ? 'Yes' : 'No'}</td>
                <td>${region.is_lesion ? 'Yes' : 'No'}</td>
                <td>${region.is_epileptic ? 'Yes' : 'No'}</td>
            </tr>
        `).join('')
        : '<tr><td colspan="9" class="muted">No mapped clinical abnormality regions were returned for the selected patients.</td></tr>';

    const topZRegionsHtml = topZRegions.length
        ? topZRegions.map(region => `
            <tr>
                <td>${escapeHtml(region.patient_id)}</td>
                <td>${escapeHtml(region.region_name)}</td>
                <td>${escapeHtml(region.n_channels)}</td>
                <td>${formatReportValue(region.max_abs_z)}</td>
                <td class="positive">${formatReportValue(region.max_z)}</td>
                <td class="negative">${formatReportValue(region.min_z)}</td>
            </tr>
        `).join('')
        : '<tr><td colspan="6" class="muted">No z-score regions were available.</td></tr>';
    const regionSpotlightHtml = spotlightRegions.length
        ? spotlightRegions.map(region => `
            <section class="region-card">
                <div>
                    <h3>${escapeHtml(region.region_name)}</h3>
                    <p class="muted">${escapeHtml(region.patient_id)}${region.clinical?.lobe ? ` · ${escapeHtml(region.clinical.lobe)}` : ''}</p>
                </div>
                <div class="region-metrics">
                    <div><span>Max |Z|</span><strong>${formatReportValue(region.max_abs_z)}</strong></div>
                    <div><span>Channels</span><strong>${formatReportValue(region.n_channels)}</strong></div>
                    <div><span>Max Z</span><strong class="positive">${formatReportValue(region.max_z)}</strong></div>
                    <div><span>Min Z</span><strong class="negative">${formatReportValue(region.min_z)}</strong></div>
                </div>
                <div class="label-row">
                    ${(region.flags.length ? region.flags : ['No clinical flag mapped'])
                        .map(flag => `<span class="${flag === 'SOZ' ? 'soz-pill' : ''}">${escapeHtml(flag)}</span>`)
                        .join('')}
                </div>
            </section>
        `).join('')
        : '<p class="muted">No key abnormality regions were available for spotlighting.</p>';

    return `<!doctype html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Neuronova iEEG Abnormality Report</title>
    <style>
        body { font-family: Inter, Arial, sans-serif; color: #172033; margin: 32px; line-height: 1.45; }
        h1, h2, h3 { margin: 0 0 12px; }
        h1 { font-size: 28px; }
        h2 { font-size: 18px; margin-top: 28px; border-bottom: 1px solid #d6dbe7; padding-bottom: 8px; }
        h3 { font-size: 15px; }
        .muted { color: #667085; }
        .header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; border-bottom: 2px solid #172033; padding-bottom: 18px; }
        .meta { font-size: 12px; color: #475467; text-align: right; }
        .brain-image { width: 100%; max-height: 620px; object-fit: contain; background: #050505; border: 1px solid #d6dbe7; border-radius: 8px; margin-top: 14px; }
        .visual-grid { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(320px, .9fr); gap: 16px; align-items: stretch; }
        .map-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 12px; margin-top: 12px; }
        .map-card { border: 1px solid #d6dbe7; border-radius: 8px; padding: 12px; background: #fbfcff; }
        .map-card svg { display: block; width: 100%; height: auto; margin-top: 8px; border-radius: 8px; }
        .empty-map { min-height: 180px; }
        .card-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; }
        .card { border: 1px solid #d6dbe7; border-radius: 8px; padding: 14px; background: #fbfcff; }
        .region-card { border: 1px solid #d6dbe7; border-left: 5px solid #dc2626; border-radius: 8px; padding: 14px; background: #fff; margin-bottom: 10px; break-inside: avoid; }
        .localization-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 16px 0; }
        .localization-summary > div { border: 1px solid #d6dbe7; border-radius: 8px; padding: 14px; background: #fbfcff; }
        .localization-summary strong { display: block; font-size: 16px; margin: 4px 0 6px; }
        .eyebrow { display: block; color: #667085; font-size: 10px; text-transform: uppercase; letter-spacing: .08em; font-weight: 700; }
        .region-metrics { display: grid; grid-template-columns: repeat(4, minmax(70px, 1fr)); gap: 8px; margin: 10px 0; }
        .region-metrics span { display: block; color: #667085; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
        .region-metrics strong { font-size: 15px; }
        .hot-channel-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; margin-top: 10px; }
        .hot-channel-card { display: grid; grid-template-columns: 28px minmax(0, 1fr) auto; gap: 8px; align-items: start; border: 1px solid #d6dbe7; border-radius: 8px; padding: 10px; background: #fff; }
        .hot-channel-card p { margin: 2px 0 0; color: #667085; font-size: 11px; }
        .hot-channel-card .rank { width: 22px; height: 22px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; background: #172033; color: #fff; font-size: 11px; font-weight: 700; }
        .hot-channel-score { font-size: 18px; font-weight: 800; }
        .mini-labels { grid-column: 2 / -1; margin-top: 2px; }
        .kv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; }
        .kv-grid span { display: block; color: #667085; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
        .kv-grid strong { display: block; font-size: 13px; margin-top: 2px; }
        .label-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
        .label-row span, .pill { border-radius: 999px; padding: 4px 8px; background: #eef2ff; color: #263b73; font-size: 11px; }
        .soz-pill { background: #fef3c7 !important; color: #92400e !important; }
        .legend { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; font-size: 11px; color: #475467; }
        .legend span { display: inline-flex; align-items: center; gap: 5px; }
        .swatch { width: 10px; height: 10px; border-radius: 999px; display: inline-block; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
        th, td { border-bottom: 1px solid #e5e7ef; padding: 8px; text-align: left; vertical-align: top; }
        th { background: #f3f5fa; color: #344054; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
        .positive { color: #b42318; font-weight: 700; }
        .negative { color: #175cd3; font-weight: 700; }
        .callout { background: #fff8e6; border: 1px solid #f4c45d; border-radius: 8px; padding: 12px; margin-top: 14px; }
        .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #d6dbe7; font-size: 11px; color: #667085; }
        @media print { body { margin: 18mm; } .brain-image { max-height: 420px; } .visual-grid, .localization-summary { display: block; } .localization-summary > div { margin-bottom: 8px; } }
    </style>
</head>
<body>
    <div class="header">
        <div>
            <h1>Neuronova iEEG Abnormality Report</h1>
            <div class="muted">Clinical decision-support summary for patient-level normative abnormalities.</div>
        </div>
        <div class="meta">
            Generated: ${escapeHtml(generatedAt)}<br/>
            Patients: ${escapeHtml(patients.join(', ') || 'None')}<br/>
            Sites: ${escapeHtml(centers.join(', ') || 'None')}<br/>
            Feature: ${escapeHtml(featureLabel || feature)}<br/>
            Threshold: |Z| ≥ ${escapeHtml(threshold)}
        </div>
    </div>

    <h2>Clinical Localization Summary</h2>
    ${localizationSummaryHtml}
    <div class="callout">
        The locator maps below are intended to answer "where are the hot channels?" more directly than the 3D viewer screenshot. They are surface-coordinate projections on a template outline, not diagnostic MRI slices. Red circles indicate positive z-score deviations, blue circles indicate negative deviations, and larger circles indicate larger absolute z-score magnitude. Diamond markers indicate clinical metadata labels such as SOZ, early spread, spikes, lesions, or epileptic contacts.
    </div>

    <h2>Anatomical Locator Maps</h2>
    <div class="legend">
        <span><i class="swatch" style="background:#dc2626"></i>Positive z-score</span>
        <span><i class="swatch" style="background:#2563eb"></i>Negative z-score</span>
        <span><i class="swatch" style="background:#f59e0b"></i>SOZ diamond</span>
        <span><i class="swatch" style="background:#a855f7"></i>Early spread diamond</span>
        <span><i class="swatch" style="background:#22c55e"></i>Spikes diamond</span>
    </div>
    <div class="map-grid">${axialMapHtml}${lateralMapHtml}</div>

    <h2>Top Z-Score Channels</h2>
    <div class="hot-channel-grid">${labeledHotChannelsHtml}</div>

    <h2>3D Viewer Snapshot</h2>
    <div class="visual-grid">
        <section>
            <h3>Current Brain View</h3>
            <p class="muted">This is the exact viewer angle at export time. Use the locator maps above for anatomical context.</p>
            ${brainImage ? `<img class="brain-image" src="${brainImage}" alt="Current Neuronova brain visualization" />` : '<p class="muted">Brain screenshot unavailable from the current viewer. Plot the z-score points and keep the region in view before exporting.</p>'}
        </section>
        <section>
            <h3>Key Abnormality Regions</h3>
            ${regionSpotlightHtml}
        </section>
    </div>

    <h2>Patient Context</h2>
    <div class="card-grid">${patientSummaryHtml}</div>

    <h2>Full Thresholded Channel Table</h2>
    <table>
        <thead><tr><th>Patient</th><th>Channel</th><th>Region</th><th>Z-score</th><th>Raw value</th><th>Surface coordinates</th></tr></thead>
        <tbody>${topChannelsHtml}</tbody>
    </table>

    <h2>Abnormality Regions From Clinical Metadata</h2>
    <table>
        <thead><tr><th>Patient</th><th>Region</th><th>Lobe</th><th>Contacts</th><th>SOZ</th><th>Early spread</th><th>Spikes</th><th>Lesion</th><th>Epileptic</th></tr></thead>
        <tbody>${clinicalRegionsHtml}</tbody>
    </table>

    <h2>Regions With Largest Normative Deviation</h2>
    <table>
        <thead><tr><th>Patient</th><th>Region</th><th>Channels</th><th>Max |Z|</th><th>Max Z</th><th>Min Z</th></tr></thead>
        <tbody>${topZRegionsHtml}</tbody>
    </table>

    <h2>Configuration</h2>
    <p>
        <span class="pill">Feature: ${escapeHtml(featureLabel || feature)}</span>
        <span class="pill">Color by direction: ${zScoreSettings.colorByDirection ? 'Yes' : 'No'}</span>
        <span class="pill">Scale by magnitude: ${zScoreSettings.scaleByMagnitude ? 'Yes' : 'No'}</span>
        <span class="pill">Threshold mode: ${zScoreSettings.thresholdMode ? 'Yes' : 'No'}</span>
    </p>

    <div class="footer">
        Research-use report generated by Neuronova. Review electrode localization, metadata quality, and clinical labels before using this summary for clinical interpretation.
    </div>
</body>
</html>`;
};
